/**
 * Phase 132 — Academy StreamX Dashboard (/livex/dashboard)
 * Home for academy_owner. Plan banner, recent streams, top moments summary,
 * Go Live CTA. Doesn't replace Stream Library (/livex/history) — this is the
 * cockpit; library is the archive.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import '@/styles/livex.css';
import {
  Radio, Trophy, Sparkles, ArrowLeft, ExternalLink, TrendingUp, Eye,
  Crown, AlertTriangle, BarChart3, History, Film,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import LivexEmptyState from '@/components/livex/LivexEmptyState';
import LivexUpgradeNudge from '@/components/livex/LivexUpgradeNudge';
import LivexActivationBanner from '@/components/livex/LivexActivationBanner';
import LivexFirstStreamCelebration from '@/components/livex/LivexFirstStreamCelebration';
import LivexNotificationBell from '@/components/livex/LivexNotificationBell';
import LivexSubscriptionStateBanner from '@/components/livex/LivexSubscriptionStateBanner';
import { LivexShimmerKpi, LivexShimmerRow } from '@/components/livex/LivexShimmer';

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }); }
  catch { return iso; }
}

export default function LivexDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ subscription: null, streams: [], highlights: [] });
  const [activation, setActivation] = useState(null); // Phase 134C
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [s, st, act] = await Promise.all([
          api.get('/livex/me/subscription'),
          api.get('/livex/streams?limit=8'),
          api.get('/livex/me/activation-status').catch(() => ({ data: null })),
        ]);
        if (!active) return;
        setData({ subscription: s.data, streams: st.data?.entries || [], highlights: [] });
        setActivation(act?.data || null);
      } catch {/* fall through */}
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen text-white px-4 sm:px-6 py-6" style={{ background: '#0a0a0f' }} data-testid="livex-dashboard-loading">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="grid lg:grid-cols-[1fr_320px] gap-4">
            <div className="h-44 livex-shimmer rounded-2xl" />
            <div className="h-44 livex-shimmer rounded-2xl" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <LivexShimmerKpi key={i} />)}
          </div>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => <LivexShimmerRow key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  const sub = data.subscription;
  const tier = sub?.plan_tier || 'pilot';
  const isPro = tier === 'pro';
  // Phase 138B — recognize Pro customers whose subscription is currently
  // past_due or suspended. plan_tier flips to 'pilot' in those states (for
  // stream-cap enforcement), but the customer's actual subscription tier is
  // still Pro. The plan card honors that intent so we don't misrepresent
  // their relationship with us as 'Free Pilot'.
  const subStatus = sub?.subscription?.status;
  const isProDunning = !isPro && sub?.subscription?.plan === 'pro' && (subStatus === 'past_due' || subStatus === 'suspended');
  const showProCard = isPro || isProDunning;
  const isSuspended = subStatus === 'suspended';
  const streamsLeft = sub?.usage?.streams_remaining;
  const streamsUsed = sub?.usage?.streams_this_month ?? 0;
  const streamsLimit = sub?.usage?.streams_per_month_limit ?? 2;
  const usagePercent = Math.min(100, Math.round((streamsUsed / streamsLimit) * 100));
  const showUpgradeNudge = !isPro && !isProDunning && (usagePercent >= 80 || streamsLeft === 0);
  const liveStreams = data.streams.filter((s) => s.status === 'live');
  const recentStreams = data.streams.slice(0, 6);

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-dashboard">
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-[#FF5A1F] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Out-Play Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-xs tracking-[0.3em] font-black text-[#FF5A1F]">STREAMX · COCKPIT</div>
            <LivexNotificationBell />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Welcome + Plan banner */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 p-6 sm:p-8 bg-[#07111F] border-2 border-[#22314F] rounded-2xl">
            <div className="text-xs tracking-[0.2em] font-black text-[#FF5A1F] mb-2">WELCOME BACK</div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-1">{user?.name || 'Coach'}</h1>
            <p className="livex-body-text mb-6">Your StreamX broadcast cockpit. Go live in one tap.</p>
            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <Link to="/streamx/studio" data-testid="livex-dashboard-go-live"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
                style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)', boxShadow: '0 8px 24px rgba(255,90,31,0.30)', minHeight: '52px' }}>
                <Radio className="h-4 w-4" /> Go Live Now
              </Link>
              <Link to="/streamx/replays" data-testid="livex-dashboard-replays"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-xl transition-all"
                style={{ minHeight: '52px' }}>
                <Film className="h-4 w-4" /> Replays
              </Link>
              <Link to="/streamx/history" data-testid="livex-dashboard-library"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-xl transition-all"
                style={{ minHeight: '52px' }}>
                <History className="h-4 w-4" /> Stream Library
              </Link>
              <Link to="/streamx/highlights" data-testid="livex-dashboard-highlights"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-xl transition-all"
                style={{ minHeight: '52px' }}>
                <Sparkles className="h-4 w-4" /> Highlights
              </Link>
            </div>
          </div>

          <div
            className="lg:w-80 p-5 rounded-2xl border-2 relative"
            style={showProCard
              ? {
                  background: 'linear-gradient(135deg, #1a0e10, #07111F)',
                  borderColor: isSuspended ? '#7f1d1d' : (isProDunning ? '#92400e' : '#FF5A1F'),
                  opacity: isProDunning ? 0.92 : 1,
                }
              : { background: '#07111F', borderColor: '#22314F' }}
            data-testid="livex-dashboard-plan-card"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-black uppercase tracking-[0.15em] text-[#94A3B8]">Your Plan</span>
              {showProCard ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.15em] text-white rounded-full"
                  style={{
                    background: isSuspended
                      ? 'linear-gradient(135deg, #7f1d1d, #b91c1c)'
                      : (isProDunning
                          ? 'linear-gradient(135deg, #92400e, #d97706)'
                          : 'linear-gradient(135deg, #dc2626, #f97316)'),
                  }}
                  data-testid={isSuspended ? 'plan-chip-suspended' : (isProDunning ? 'plan-chip-past-due' : 'plan-chip-pro')}
                >
                  <Crown className="h-3 w-3" /> {isSuspended ? 'Pro · Paused' : (isProDunning ? 'Pro · Past Due' : 'Pro')}
                </span>
              ) : (
                <span className="text-[10px] font-black uppercase tracking-[0.15em] px-2 py-0.5 bg-slate-700 text-white rounded-full">Pilot</span>
              )}
            </div>
            <div className="text-2xl font-black text-white mb-3">{showProCard ? 'StreamX Pro' : 'Free Pilot'}</div>
            {!showProCard && (
              <>
                <div className="mb-3">
                  <div className="flex items-baseline justify-between">
                    <span className="text-xs font-bold text-[#94A3B8]">Streams this month</span>
                    <span className="text-sm font-black text-white tabular-nums">{sub.usage.streams_this_month}/{sub.usage.streams_per_month_limit}</span>
                  </div>
                  <div className="h-2 mt-1.5 bg-[#0c1a2e] rounded-full overflow-hidden">
                    <div className="h-full bg-[#FF5A1F]" style={{ width: `${Math.min(100, (sub.usage.streams_this_month / sub.usage.streams_per_month_limit) * 100)}%` }} />
                  </div>
                </div>
                {streamsLeft === 0 && (
                  <div className="mb-3 p-2.5 bg-rose-950 border-2 border-rose-700 rounded-lg text-rose-100 text-xs font-bold flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" /> Monthly limit reached. Upgrade to keep streaming.
                  </div>
                )}
                <Link to="/streamx/subscription" data-testid="livex-dashboard-upgrade-link"
                  className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2.5 text-sm font-bold text-white rounded-lg border-2 border-[#FF5A1F] transition-all"
                  style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
                  <Sparkles className="h-3.5 w-3.5" /> Upgrade to Pro
                </Link>
              </>
            )}
            {showProCard && (
              <Link to="/streamx/subscription" className="inline-flex items-center justify-center gap-1.5 w-full px-3 py-2.5 text-sm font-bold text-white bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all">
                {isProDunning ? 'Resolve & manage subscription' : 'Manage subscription'}
              </Link>
            )}
          </div>
        </div>

        {/* Phase 138B — Subscription state banner (suspended / past_due / renewing soon).
            Renders nothing for healthy active subscriptions. Top priority above
            activation banners since dunning state always supersedes activation. */}
        <LivexSubscriptionStateBanner subscription={sub} />

        {/* Phase 134C — Activation funnel banners. Show the activation banner
            for verified-but-not-activated academies (>=48h). Show the
            first-stream celebration for academies who broadcast within the
            last 24h (one-shot via localStorage). Both are mutually exclusive
            with the upgrade nudge below. */}
        {activation?.should_show_activation_banner && (
          <LivexActivationBanner accountAgeHours={activation.account_age_hours} />
        )}
        {activation?.first_stream_just_now && activation?.has_first_stream && (
          <LivexFirstStreamCelebration academyId={user?.academy_id} />
        )}

        {/* Upgrade nudge — appears for pilot academies hitting 80% usage.
            Suppressed during the activation phase so we don't pile pressure
            on academies who haven't even broadcast yet. */}
        {showUpgradeNudge && !activation?.should_show_activation_banner && (
          <LivexUpgradeNudge usage={streamsUsed} remaining={streamsLeft} percent={usagePercent} />
        )}

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <Radio className="h-4 w-4 text-rose-500 mb-1.5" />
            <div className="text-2xl font-black text-white tabular-nums">{liveStreams.length}</div>
            <div className="livex-label" style={{ marginBottom: 0 }}>Live Now</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <BarChart3 className="h-4 w-4 text-[#FF5A1F] mb-1.5" />
            <div className="text-2xl font-black text-white tabular-nums">{data.streams.length}</div>
            <div className="livex-label" style={{ marginBottom: 0 }}>Recent Streams</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <Eye className="h-4 w-4 text-[#FF5A1F] mb-1.5" />
            <div className="text-2xl font-black text-white tabular-nums">{data.streams.reduce((a, s) => Math.max(a, s.peak_viewers || 0), 0)}</div>
            <div className="livex-label" style={{ marginBottom: 0 }}>Best Peak</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <TrendingUp className="h-4 w-4 text-[#FF5A1F] mb-1.5" />
            <div className="text-2xl font-black text-white tabular-nums">{data.streams.reduce((a, s) => a + (s.viewer_count_estimate || 0), 0)}</div>
            <div className="livex-label" style={{ marginBottom: 0 }}>Total Viewers Now</div>
          </div>
        </div>

        {/* Recent streams */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-black text-white">Recent broadcasts</h2>
            <Link to="/streamx/history" data-testid="livex-dashboard-view-all" className="text-sm font-bold text-[#FF5A1F] hover:underline inline-flex items-center gap-1">
              View all <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          {recentStreams.length === 0 ? (
            <LivexEmptyState
              testid="livex-dashboard-streams-empty"
              icon={Radio}
              title="No broadcasts yet"
              body="Your first stream is a tap away. Set up takes 60 seconds, then your match is live for parents and scouts to watch."
              ctaLabel="Go Live Now"
              ctaTo="/livex/studio"
              ctaIcon={Radio}
              secondaryLabel="See pricing"
              secondaryTo="/livex/pricing"
              compact
            />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {recentStreams.map((s) => (
                <Link key={s.id} to={`/livex/watch/${s.id}`} target="_blank" rel="noopener noreferrer"
                  data-testid={`livex-dashboard-stream-${s.id}`}
                  className="livex-card-lift block p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl hover:border-[#FF5A1F]">
                  <div className="flex items-center justify-between mb-2">
                    {s.status === 'live' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white bg-rose-600 rounded">
                        <span className="h-1 w-1 rounded-full bg-white animate-pulse" /> LIVE
                      </span>
                    ) : (
                      <span className="text-[10px] font-black uppercase tracking-wider text-[#94A3B8]">{formatDate(s.created_at)}</span>
                    )}
                    {s.sport && <span className="text-[10px] font-bold text-white capitalize">{s.sport}</span>}
                  </div>
                  <h3 className="text-sm font-black text-white truncate mb-1">{s.title}</h3>
                  <div className="text-[11px] font-bold text-[#94A3B8] flex items-center gap-2">
                    <Eye className="h-3 w-3" /> Peak {s.peak_viewers || 0}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Brand page CTA */}
        {user?.academy_slug && (
          <div className="p-5 bg-gradient-to-br from-[#1a0e10] to-[#07111F] border-2 border-[#3a1a1f] rounded-2xl flex items-center justify-between gap-4 flex-wrap" data-testid="livex-dashboard-brand-page">
            <div className="flex items-center gap-3">
              <Trophy className="h-8 w-8 text-[#FF5A1F]" />
              <div>
                <div className="text-sm font-black text-white">Your academy brand page is live</div>
                <div className="text-xs font-bold text-[#94A3B8]">Auto-aggregated highlight reel + SEO-indexed showcase</div>
              </div>
            </div>
            <Link to={`/livex/academy/${user.academy_slug}/top-moments`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-white border-2 border-[#FF5A1F] rounded-lg"
              style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <Sparkles className="h-4 w-4" /> View Brand Page
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
